import { FeatureKey, TAB_MODULES } from 'common/constants/permission'
import uniq from 'lodash/uniq'
import { RoleName } from 'modules/member/constants'
import { PondTab } from 'modules/pond/constants'
import { PATH_DASHBOARD } from 'routes/paths'

import { convertFeatureKeyToPondTab, getPondTabPath } from 'utils/pond_tabs'

import { useUserInfo } from './queries'

export const useAllowFeatures = () => {
  const { data } = useUserInfo()
  const features = (data?.user_role.permissions ?? []).filter((el) => {
    const isRead = el.crud_permission[1]
    return isRead
  })
  return features
}
const DASHBOARD_LANDING_CANDIDATES: { key: FeatureKey; path: string }[] = [
  { key: FeatureKey.Farm, path: PATH_DASHBOARD.farms.root },
  { key: FeatureKey.Pond, path: PATH_DASHBOARD.ponds.root },
  { key: FeatureKey.Iot, path: PATH_DASHBOARD.iot.root },
  { key: FeatureKey.OrganizationDashboard, path: PATH_DASHBOARD.organization.root },
  { key: FeatureKey.PondComparison, path: PATH_DASHBOARD.comparison.root },
  { key: FeatureKey.Inventory, path: PATH_DASHBOARD.inventory.product },
]


export const useFirstAllowedDashboardPath = () => {
  const features = useAllowFeatures()
  const match = DASHBOARD_LANDING_CANDIDATES.find((candidate) =>
    features.some((feature) => feature.feature.key === candidate.key)
  )
  return match?.path ?? PATH_DASHBOARD.shrimpPrice.root
}

export const useAllowPondTabs = () => {
  const features = useAllowFeatures()
  const featuresInTabs = features
    ?.filter(
      (el) => TAB_MODULES.includes(el.feature.key as FeatureKey) && el.crud_permission[1] === 1
    )
    .sort(
      (a, b) =>
        TAB_MODULES.indexOf(a.feature.key as FeatureKey) -
        TAB_MODULES.indexOf(b.feature.key as FeatureKey)
    )

  if (featuresInTabs.length > 0) {
    const results = featuresInTabs.map((el) =>
      convertFeatureKeyToPondTab(el.feature.key as FeatureKey)
    )
    return uniq(results)
  }
  return []
}

export const useFirstAllowedPondPath = (farmId: string, pondId: string) => {
  const allowTabs = useAllowPondTabs()
  const firstAllowTab = allowTabs[0] ?? PondTab.Dashboard
  return getPondTabPath(firstAllowTab, farmId, pondId)
}

export const usePermitByFeature = (key: string) => {
  const { data } = useUserInfo()
  const feature = (data?.user_role.permissions ?? []).find((p) => p.feature.key === key)
  return feature?.crud_permission[1] ? true : false
}

export const usePermitByFeatures = (crud: number, keys: string[]) => {
  const { data } = useUserInfo()
  const features = (data?.user_role.permissions ?? []).filter((p) => keys.includes(p.feature.key))
  const allow = features.every((f) => f.crud_permission[crud] === 1)
  return allow
}

export const usePermitSomeFeatures = (keys: string[]) => {
  const { data } = useUserInfo()
  const features = (data?.user_role.permissions ?? []).filter((p) => keys.includes(p.feature.key))
  const allow = features.some((f) => f.crud_permission[1] === 1)
  return allow
}

export const usePermitToActionFeature = (actions: number[], featureKey: string) => {
  const { data } = useUserInfo()
  const feature = (data?.user_role.permissions ?? []).find((p) => featureKey === p.feature.key)
  const allow = actions.every((a) => feature?.crud_permission?.[a] === 1)
  return allow
}

export const usePermitSomeActionToFeature = (actions: number[], featureKey: string) => {
  const { data } = useUserInfo()
  const feature = (data?.user_role.permissions ?? []).find((p) => featureKey === p.feature.key)
  const allow = actions.some((a) => feature?.crud_permission?.[a] === 1)
  return allow
}

export const useGetCRUDByFeature = (featureKey: string) => {
  const { data } = useUserInfo()
  const feature = (data?.user_role.permissions ?? []).find((p) => p.feature.key === featureKey)
  return feature?.crud_permission
}

export const useIsPrimaryOwnerAndAdmin = () => {
  const { data } = useUserInfo()

  return data?.user_role.name === RoleName.PrimaryOwner || data?.user_role.name === RoleName.Admin
}

export const useIsPrimaryOwnerAndSecondaryOwnerAndAdmin = () => {
  const { data } = useUserInfo()
  return (
    data?.user_role.name === RoleName.PrimaryOwner ||
    data?.user_role.name === RoleName.Admin ||
    data?.user_role.name === RoleName.SecondaryOwner
  )
}
